import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { NotificationService } from '../../notification/notification.service';
import {
  SaveWorktimeEntryRequest,
  WorktimeConfig,
  WorktimeEntry,
  WorktimeEntryType,
  WorktimePlannedDay,
  WorktimeService,
  WorktimeSummary,
} from '../api';

// ============================================================================
// Munkaidő / Munkaidő admin — közös view-logika.
// Forrás: erp-design/screens/08-munkaido.html + 23-logic.js worktimeVals(),
// wtHours/wtFt/wtHL — 1:1 átültetve. A bér/összesítő képletek kliens oldali
// FALLBACK-ek: ha a szerver ad summary-t (GET worktime/summary), azt jelenítjük
// meg (CONTRACTS.md 1. pont — a szerver számol).
// ============================================================================

export const WT_MONTH_NAMES = [
  'január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december',
];

export const WT_DAY_HEADS = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];

const WT_DAY_NAMES = ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat'];

/** Avatar színpaletta a prototípusból (supUsers) — determinisztikus név alapján. */
const WT_AVATAR_COLORS = ['#C77B9B', '#5E7FA8', '#6E9663'];

/** Konfig hiányában (404 — a backend párhuzamosan készül) használt alapértékek. */
const WT_DEFAULT_CONFIG = {
  dailyRequiredHours: 8,
  annualVacationDays: 25,
  usedPriorVacationDays: 0,
  hourlyWage: 0,
  vacationDayHours: 8,
};

/** 23-logic.js wtHours(en) 1:1 — (vége − kezdés) − 30 perc ebédszünet. */
export function wtHours(start: string | null | undefined, end: string | null | undefined, type: WorktimeEntryType): number {
  if (type !== 'work' || !start || !end) { return 0; }
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if (isNaN(sh) || isNaN(eh)) { return 0; }
  const h = (eh * 60 + (em || 0) - sh * 60 - (sm || 0)) / 60 - 0.5; // 30 perc ebédszünet
  return Math.max(0, Math.round(h * 100) / 100);
}

/** 23-logic.js wtFt(n) 1:1 — ezres szóközökkel, ' Ft' utótaggal. */
export function wtFt(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' Ft';
}

/** 23-logic.js wtHL(h) 1:1 — óra címke egy tizedessel ('41.6 ó' / '8 ó'). */
export function wtHL(h: number): string {
  const r = Math.round(h * 10) / 10;
  return (r % 1 === 0 ? r.toFixed(0) : r.toFixed(1)) + ' ó';
}

/** Determinisztikus avatar háttérszín (a prototípus fix supUsers térképe helyett). */
export function wtAvatarColor(name: string | null | undefined): string {
  if (!name) { return '#9AA098'; }
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return WT_AVATAR_COLORS[hash % WT_AVATAR_COLORS.length];
}

/** Monogram (1 betű) — 23-logic.js supAvatar. */
export function wtInitial(name: string | null | undefined): string {
  return (name || '?').slice(0, 1).toUpperCase();
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** 'HH:mm' → perc éjféltől. */
function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function dateKey(year: number, month1: number, day: number): string {
  return year + '-' + pad2(month1) + '-' + pad2(day);
}

/** 'HH:mm:ss' → 'HH:mm' (a backend időformátumára védekezve). */
function hm(t: string | null | undefined): string {
  return (t || '').slice(0, 5);
}

export type WtCellKind = '' | 'work-done' | 'work-open' | 'vacation' | 'sick' | 'absence' | 'planned';

/** Naptár-cella felirat: 'Távol 8–12' vagy egész napnál 'Távollét'. */
function wtAbsencePill(start: string | null | undefined, end: string | null | undefined): string {
  return start && end ? 'Távol ' + wtSpanLabel(start, end) : 'Távollét';
}

/** Távollét idősáv címke: '8–12' vagy 'egész nap'. */
export function wtSpanLabel(start: string | null | undefined, end: string | null | undefined): string {
  if (!start || !end) { return 'egész nap'; }
  const short = (t: string) => t.slice(0, 5).replace(/^0/, '').replace(/:00$/, '');
  return short(start) + '–' + short(end);
}

/**
 * Foglalkoztatási típus (backend: users.employment_type).
 * Külsősnél nincs szabadság-/betegszabadság-elszámolás.
 */
export type WtEmploymentType = 'employee' | 'external';

/**
 * A szerver summary-ja + az employmentType mező (a WorktimeSummary interfész a
 * stokker/api-ban még nem tartalmazza — lásd a záró jelentést).
 */
export type WtSummaryVm = WorktimeSummary & { employmentType?: WtEmploymentType };

export interface WtCellVm {
  blank: boolean;
  key: string;
  num: number;
  weekend: boolean;
  today: boolean;
  kind: WtCellKind;
  timeLabel: string;
  typeLabel: string;
  pillClass: string;
  hasComment: boolean;
}

export interface WtKpiVm {
  label: string;
  value: string;
  sub: string;
  valColor: string;
}

export interface WtDraft {
  type: WorktimeEntryType;
  start: string;
  end: string;
  comment: string;
}

/**
 * Közös bázis a Munkaidő (saját nézet) és Munkaidő admin komponensekhez.
 * Szándékosan nincs rajta dekorátor és lifecycle interface — a leszármazott
 * komponens hívja az initWorktime()/reload()-ot a saját ngOnInit-jéből.
 */
export abstract class WorktimeBase {

  year: number;
  /** 0–11, mint a prototípus wtMonth-ja. */
  month: number;

  readonly dayHeads = WT_DAY_HEADS;
  readonly typeChips: { t: WorktimeEntryType; label: string }[] = [
    { t: 'work', label: 'Munkanap' },
    { t: 'vacation', label: 'Szabadság' },
    { t: 'sick', label: 'Betegszabadság' },
    { t: 'absence', label: 'Távollét' },
  ];

  cells: WtCellVm[] = [];
  kpis: WtKpiVm[] = [];
  loading = true;
  saving = false;

  /**
   * A nézett user foglalkoztatási típusa — a szerver summary-jából (null =
   * még nem tudjuk). A leszármazott előre beállíthatja (pl. admin a
   * user-listából), hogy a KPI-rács ne villanjon.
   */
  employmentType: WtEmploymentType | null = null;

  // Nap modál (wtDayModal / wtDraft)
  modalKey: string | null = null;
  modalHadEntry = false;
  /** A megnyitott nap tervezett távolléte (ha van) — a mentés felülírja. */
  modalPlan: WorktimePlannedDay | null = null;
  draft: WtDraft | null = null;

  protected entries = new Map<string, WorktimeEntry>();
  /** Tervezett távollétek a hónapra — csak a bejegyzés nélküli napokra. */
  protected planned = new Map<string, WorktimePlannedDay>();
  protected summary: WtSummaryVm | null = null;
  protected config: WorktimeConfig | null = null;

  private todayKey = '';
  private loadSeq = 0;

  constructor(
    protected worktime: WorktimeService,
    protected auth: AuthService,
    protected notification: NotificationService,
    protected confirmDialog: ConfirmDialogService,
  ) {
    const now = new Date();
    this.year = now.getFullYear();
    this.month = now.getMonth();
  }

  /** Admin: a kiválasztott user id-ja; saját nézet: undefined (a token usere). */
  protected abstract targetUserId(): number | undefined;

  // --------------------------------------------------------------------------
  // Foglalkoztatási típus (Alkalmazott / Külsős)
  // --------------------------------------------------------------------------

  /** A tervezett távollétek panel userId-je (saját nézetben undefined). */
  get plansUserId(): number | undefined {
    return this.targetUserId();
  }

  /** Külsős munkatárs: nincs szabadság-/betegszabadság-elszámolása. */
  get isExternal(): boolean {
    return this.employmentType === 'external';
  }

  /**
   * A nap-modálban választható típusok. Külsősnél csak a „Munkanap” — a
   * korábbról meglévő (legacy) szabadság/beteg bejegyzés típusa viszont
   * látszik, hogy a UI ne törjön el.
   */
  get visibleTypeChips(): { t: WorktimeEntryType; label: string }[] {
    if (!this.isExternal) { return this.typeChips; }
    return this.typeChips.filter(c => c.t === 'work' || c.t === 'absence' || this.draft?.type === c.t);
  }

  // --------------------------------------------------------------------------
  // Hónap navigáció (wtPrevMonth / wtNextMonth — évhatáron is átlép)
  // --------------------------------------------------------------------------

  get monthLabel(): string {
    return this.year + '. ' + WT_MONTH_NAMES[this.month];
  }

  prevMonth(): void {
    this.closeDay();
    if (this.month === 0) {
      this.month = 11;
      this.year--;
    } else {
      this.month--;
    }
    this.reload();
  }

  nextMonth(): void {
    this.closeDay();
    if (this.month === 11) {
      this.month = 0;
      this.year++;
    } else {
      this.month++;
    }
    this.reload();
  }

  // --------------------------------------------------------------------------
  // Betöltés — bejegyzések + havi összesítő + konfig; 404 → üres állapot
  // --------------------------------------------------------------------------

  reload(): void {
    const seq = ++this.loadSeq;
    this.loading = true;
    // Azonnali üres naptár-váz az új hónapra, amíg jön az adat.
    this.entries = new Map<string, WorktimeEntry>();
    this.planned = new Map<string, WorktimePlannedDay>();
    this.summary = null;
    this.rebuild();

    const userId = this.targetUserId();
    const keepConfig = this.config;
    forkJoin({
      entries: this.worktime.getEntries(this.year, this.month + 1, userId).pipe(
        catchError(() => of([] as WorktimeEntry[])),
      ),
      summary: this.worktime.getSummary(this.year, this.month + 1, userId).pipe(
        catchError(() => of(null as WtSummaryVm | null)),
      ),
      planned: this.worktime.getPlannedDays(this.year, this.month + 1, userId).pipe(
        catchError(() => of([] as WorktimePlannedDay[])),
      ),
      // Konfig-hiba esetén a már ismert konfigot tartjuk meg (hónapváltásnál ne vesszen el).
      config: this.worktime.getConfig(userId).pipe(
        catchError(() => of(keepConfig)),
      ),
    }).subscribe(({ entries, summary, config, planned }) => {
      if (seq !== this.loadSeq) { return; }
      this.entries = new Map(
        (Array.isArray(entries) ? entries : []).map(e => [String(e.date || '').slice(0, 10), e]),
      );
      this.planned = new Map((Array.isArray(planned) ? planned : []).map(p => [p.date, p]));
      const sum = summary as WtSummaryVm | null;
      this.summary = sum;
      this.config = config;
      // A foglalkoztatási típust a szerver mondja meg; hiba esetén az eddig
      // ismert (vagy a leszármazott által beállított) érték marad.
      if (sum?.employmentType) {
        this.employmentType = sum.employmentType;
      }
      this.loading = false;
      this.rebuild();
    });
  }

  /** Egy bejegyzés elszámolt órája — a szerver értéke, kliens képlet fallbackkel. */
  protected entryHours(en: WorktimeEntry): number {
    if (en.workedHours != null) { return en.workedHours; }
    return wtHours(en.startTime, en.endTime, en.type);
  }

  protected effectiveConfig(): { daily: number; vacation: number; usedPrior: number; wage: number; vacDayHours: number } {
    const c = this.config;
    return {
      daily: c?.dailyRequiredHours ?? WT_DEFAULT_CONFIG.dailyRequiredHours,
      vacation: c?.annualVacationDays ?? WT_DEFAULT_CONFIG.annualVacationDays,
      usedPrior: c?.usedPriorVacationDays ?? WT_DEFAULT_CONFIG.usedPriorVacationDays,
      wage: c?.hourlyWage ?? WT_DEFAULT_CONFIG.hourlyWage,
      vacDayHours: c?.vacationDayHours ?? c?.dailyRequiredHours ?? WT_DEFAULT_CONFIG.vacationDayHours,
    };
  }

  // --------------------------------------------------------------------------
  // Naptár + KPI-k újraszámítása — 23-logic.js worktimeVals() 1:1
  // --------------------------------------------------------------------------

  protected rebuild(): void {
    const now = new Date();
    this.todayKey = dateKey(now.getFullYear(), now.getMonth() + 1, now.getDate());

    const cfg = this.effectiveConfig();
    const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();
    const firstDow = (new Date(this.year, this.month, 1).getDay() + 6) % 7;

    // Havi összesítés (kliens fallback)
    let workedH = 0;
    let workdays = 0;
    let vacDays = 0;
    let sickDays = 0;
    let absenceH = 0;
    let absenceDays = 0;
    let openDays = 0;
    const addAbsence = (start: string | null | undefined, end: string | null | undefined) => {
      if (start && end) { absenceH += Math.max(0, (toMinutes(end) - toMinutes(start)) / 60); }
      else { absenceDays++; absenceH += cfg.daily; }
    };

    const cells: WtCellVm[] = [];
    for (let i = 0; i < firstDow; i++) {
      cells.push({
        blank: true, key: 'b' + i, num: 0, weekend: false, today: false,
        kind: '', timeLabel: '', typeLabel: '', pillClass: '', hasComment: false,
      });
    }

    for (let dNum = 1; dNum <= daysInMonth; dNum++) {
      const key = dateKey(this.year, this.month + 1, dNum);
      const dow = (firstDow + dNum - 1) % 7;
      const weekend = dow >= 5;
      if (dow < 5) { workdays++; }

      const en = this.entries.get(key);
      let kind: WtCellKind = '';
      let timeLabel = '';
      let typeLabel = '';
      let pillClass = '';
      if (en) {
        if (en.type === 'work') {
          const h = this.entryHours(en);
          workedH += h;
          if (en.endTime) {
            timeLabel = hm(en.startTime) + ' – ' + hm(en.endTime);
            typeLabel = wtHL(h);
            kind = 'work-done';
            pillClass = 'wt-pill--done';
          } else if (key < this.todayKey) {
            // Múltbeli nap vége nélkül — 0 órával számít, pótolni kell.
            openDays++;
            timeLabel = hm(en.startTime) + ' – ?';
            typeLabel = 'Nincs vége';
            kind = 'work-open';
            pillClass = 'wt-pill--missing';
          } else {
            timeLabel = hm(en.startTime) + ' – …';
            typeLabel = 'Dolgozik';
            kind = 'work-open';
            pillClass = 'wt-pill--working';
          }
        } else if (en.type === 'absence') {
          addAbsence(en.startTime, en.endTime);
          kind = 'absence';
          typeLabel = wtAbsencePill(en.startTime, en.endTime);
          pillClass = 'wt-pill--absence';
        } else if (en.type === 'vacation') {
          vacDays++;
          kind = 'vacation';
          typeLabel = 'Szabadság';
          pillClass = 'wt-pill--vacation';
        } else {
          sickDays++;
          kind = 'sick';
          typeLabel = 'Betegszab.';
          pillClass = 'wt-pill--sick';
        }
      }
      const plan = en ? undefined : this.planned.get(key);
      if (plan) {
        addAbsence(plan.startTime, plan.endTime);
        kind = 'planned';
        typeLabel = wtAbsencePill(plan.startTime, plan.endTime);
        pillClass = 'wt-pill--planned';
      }
      cells.push({
        blank: false, key, num: dNum, weekend, today: key === this.todayKey,
        kind, timeLabel, typeLabel, pillClass,
        hasComment: !!((en && en.comment) || plan?.comment),
      });
    }
    this.cells = cells;

    // KPI-k — a szerver summary-ja az elsődleges, a kliens képlet csak fallback.
    const s = this.summary;
    const worked = s ? s.workedHours : Math.round(workedH * 100) / 100;
    const requiredH = s ? s.requiredHours : workdays * cfg.daily;
    const vacInMonth = s ? s.vacationDaysInMonth : vacDays;
    const sickInMonth = s ? s.sickDaysInMonth : sickDays;
    const absDays = s?.absenceDaysInMonth ?? absenceDays;
    const absHours = s?.absenceHoursInMonth ?? Math.round(absenceH * 100) / 100;
    const open = s?.openDays ?? openDays;
    const vacUsed = s ? s.vacationDaysUsedThisYear : vacDays + cfg.usedPrior;
    const vacTotal = s ? s.vacationDaysTotal : cfg.vacation;
    const vacLeft = s ? s.vacationDaysLeft : vacTotal - vacUsed;
    // Várható bruttó bér = ledolgozott órák × órabér + szabadnapok a konfig
    // szerint + betegszabadság 70% (23-logic.js worktimeVals salary képlete).
    const salary = s ? s.expectedGrossSalary
      : worked * cfg.wage + vacInMonth * cfg.vacDayHours * cfg.wage + sickInMonth * cfg.daily * cfg.wage * 0.7;

    const workedKpi: WtKpiVm = {
      label: 'Ledolgozott óra',
      value: wtHL(worked),
      sub: 'kötelező: ' + wtHL(requiredH) + (open ? ' · ' + open + ' nap vége hiányzik' : ''),
      valColor: worked >= requiredH ? '#4C7A2E' : '#21252B',
    };
    const absenceKpi: WtKpiVm = {
      label: 'Távollét',
      value: wtHL(absHours),
      sub: absDays ? absDays + ' egész nap · tervezettel együtt' : 'tervezettel együtt',
      valColor: '#21252B',
    };
    const salaryKpi: WtKpiVm = {
      label: 'Várható bruttó bér',
      value: wtFt(salary),
      // Órabér nélkül a 0 Ft magyarázat nélkül félrevezető — jelezzük az okát.
      sub: cfg.wage ? wtFt(cfg.wage).replace(' Ft', '') + ' Ft/óra' : 'nincs órabér beállítva',
      valColor: '#21252B',
    };

    // Külsősnél nincs szabadság-/betegszabadság-elszámolás → csak 2 KPI.
    this.kpis = this.isExternal
      ? [workedKpi, absenceKpi, salaryKpi]
      : [
        workedKpi,
        {
          label: 'Szabadság',
          value: vacLeft + ' nap',
          sub: vacUsed + ' / ' + vacTotal + ' felhasználva',
          valColor: vacLeft < 5 ? '#B77A28' : '#21252B',
        },
        {
          label: 'Betegszabadság',
          value: sickInMonth + ' nap',
          sub: 'ebben a hónapban',
          valColor: '#21252B',
        },
        absenceKpi,
        salaryKpi,
      ];
  }

  // --------------------------------------------------------------------------
  // Nap modál (wtDayModal / wtDraft handlerek)
  // --------------------------------------------------------------------------

  openDay(cell: WtCellVm): void {
    if (cell.blank) { return; }
    const en = this.entries.get(cell.key);
    const isToday = cell.key === this.todayKey;
    this.modalHadEntry = !!en;
    this.modalPlan = en ? null : (this.planned.get(cell.key) || null);
    // Alapérték a prototípusból: új napnál 08:00 kezdés; a mai napnál a vége
    // üres ("még dolgozol"), múltbeli/jövőbeli napnál 16:30.
    this.draft = en
      ? {
        type: en.type,
        start: hm(en.startTime) || '08:00',
        end: hm(en.endTime),
        comment: en.comment || '',
      }
      : { type: 'work', start: '08:00', end: isToday ? '' : '16:30', comment: '' };
    if (en?.type === 'absence' && !en.startTime) { this.draft.start = ''; }
    this.modalKey = cell.key;
  }

  closeDay(): void {
    this.modalKey = null;
    this.modalHadEntry = false;
    this.modalPlan = null;
    this.draft = null;
  }

  get modalTitle(): string {
    if (!this.modalKey) { return ''; }
    const d = new Date(this.modalKey + 'T00:00:00');
    return d.getFullYear() + '. ' + WT_MONTH_NAMES[d.getMonth()] + ' ' + d.getDate() + '. — ' + WT_DAY_NAMES[d.getDay()];
  }

  get modalPlanLabel(): string {
    const p = this.modalPlan;
    return p ? 'Tervezett távollét: ' + wtSpanLabel(p.startTime, p.endTime) + (p.comment ? ' — ' + p.comment : '')
      + '. Ha mentesz erre a napra, az felülírja.' : '';
  }

  get draftDone(): boolean {
    return !!(this.draft && this.draft.end);
  }

  get draftHours(): string {
    const d = this.draft;
    if (!d || d.type !== 'work') { return ''; }
    if (!d.end) { return 'Folyamatban — a munka vége még nincs beírva.'; }
    if (this.draftInvalidRange) { return 'A munka vége nem lehet korábbi a kezdésnél.'; }
    return 'Elszámolt idő: ' + wtHL(wtHours(d.start, d.end, 'work')) + ' (30 perc ebédszünettel)';
  }

  /** Munkanap, ahol a vége nem későbbi a kezdésnél — a szerver is elutasítaná. */
  get draftInvalidRange(): boolean {
    const d = this.draft;
    if (!d || (d.type !== 'work' && d.type !== 'absence') || !d.start || !d.end) { return false; }
    return toMinutes(d.end) <= toMinutes(d.start);
  }

  setDraftType(t: WorktimeEntryType): void {
    // Külsősnél nincs szabadság-elszámolás — a szerver is elutasítaná.
    if (this.isExternal && t !== 'work' && t !== 'absence') { return; }
    if (!this.draft) { return; }
    // Távollétnél az üres idősáv = egész nap — váltáskor ezzel indulunk.
    if (t === 'absence' && this.draft.type !== 'absence') { this.draft.start = ''; this.draft.end = ''; }
    if (t === 'work' && !this.draft.start) { this.draft.start = '08:00'; }
    this.draft.type = t;
  }

  onStartChange(event: Event): void {
    if (this.draft) { this.draft.start = (event.target as HTMLInputElement).value; }
  }

  onEndChange(event: Event): void {
    if (this.draft) { this.draft.end = (event.target as HTMLInputElement).value; }
  }

  onCommentChange(event: Event): void {
    if (this.draft) { this.draft.comment = (event.target as HTMLTextAreaElement).value; }
  }

  saveDay(): void {
    if (!this.modalKey || !this.draft || this.saving) { return; }
    const d = this.draft;
    if (this.draftInvalidRange) {
      this.notification.show('A munka vége nem lehet korábbi a kezdésnél.', 'danger');
      return;
    }
    if (d.type === 'work' && !d.start) {
      this.notification.show('Munkanaphoz kötelező a kezdés időpontja.', 'danger');
      return;
    }
    if (d.type === 'absence' && !!d.start !== !!d.end) {
      this.notification.show('Részleges távollétnél add meg a kezdést és a véget is — egész naphoz hagyd üresen mindkettőt.', 'danger');
      return;
    }
    const withTimes = d.type === 'work' || d.type === 'absence';
    const request: SaveWorktimeEntryRequest = {
      date: this.modalKey,
      type: d.type,
      startTime: withTimes ? (d.start || null) : null,
      endTime: withTimes ? (d.end || null) : null,
      comment: d.comment,
    };
    const userId = this.targetUserId();
    if (userId != null) { request.userId = userId; }
    this.saving = true;
    this.worktime.saveEntry(request).subscribe({
      next: () => {
        this.saving = false;
        this.closeDay();
        this.reload();
      },
      error: (err) => {
        this.saving = false;
        this.notification.show(wtErrorText(err, 'A bejegyzés mentése nem sikerült.'), 'danger');
      },
    });
  }

  /** Törlés csak megerősítés után — a nap bejegyzése véglegesen elvész. */
  deleteDay(): void {
    if (!this.modalKey || this.saving) { return; }
    const title = this.modalTitle;
    this.confirmDialog
      .open({
        title: 'Bejegyzés törlése',
        message: `A(z) ${title} nap bejegyzése véglegesen törlődik. Biztos törlöd?`,
        confirmLabel: 'Törlés',
        cancelLabel: 'Mégse',
        tone: 'danger',
      })
      .subscribe((result) => {
        if (!result?.confirmed || !this.modalKey) { return; }
        this.saving = true;
        this.worktime.deleteEntry(this.modalKey, this.targetUserId()).subscribe({
          next: () => {
            this.saving = false;
            this.closeDay();
            this.reload();
          },
          error: (err) => {
            this.saving = false;
            this.notification.show(wtErrorText(err, 'A bejegyzés törlése nem sikerült.'), 'danger');
          },
        });
      });
  }
}

/** A szerver magyar hibaüzenete, ha van; különben az általános szöveg. */
export function wtErrorText(err: unknown, fallback: string): string {
  const message = (err as { error?: { message?: string | string[] } })?.error?.message;
  if (Array.isArray(message) && message.length) { return message.join(' '); }
  if (typeof message === 'string' && message.trim()) { return message; }
  return fallback;
}
